/**
 * ============================================================================
 * SHARE LINKS COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Enhanced share links component with modern design
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLinks } from '../../hooks';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import { useCopyToClipboard } from '../../hooks';
import Button from '../common/Button';
import Card from '../common/Card';
import Input from '../common/Input';
import QRCode from '../links/QRCode';
import LinkPreview from '../links/LinkPreview';

/**
 * Share Links Component
 * @param {Object} props
 * @param {Object} props.transaction - Transaction object
 * @returns {JSX.Element}
 */
const ShareLinks = ({ transaction }) => {
  const navigate = useNavigate();
  const { generateShortLink, getSocialSharingLinks } = useLinks();
  const { showSuccess, showError } = useToast();
  const { formatCurrency, language } = useI18n();
  const { copy, copied } = useCopyToClipboard();

  const [shortLink, setShortLink] = useState(transaction?.shortLink || '');
  const [loading, setLoading] = useState(false);

  // Generate short link if not exists
  React.useEffect(() => {
    const generateLink = async () => {
      if (!shortLink && transaction?.id) {
        try {
          setLoading(true);
          const link = await generateShortLink(transaction.id);
          setShortLink(link.shortUrl || link.url);
        } catch (error) {
          console.error('Generate link error:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    generateLink();
  }, [transaction, shortLink, generateShortLink]);

  const handleCopyLink = async () => {
    try {
      await copy(shortLink);
      showSuccess('Link copied to clipboard!');
    } catch (error) {
      showError('Failed to copy link');
    }
  };

  const socialLinks = getSocialSharingLinks(shortLink, transaction);

  const handleShare = async (platform) => {
    const link = socialLinks[platform];
    if (link) {
      try {
        // Track share
        if (transaction?.id) {
          await trackLinkShare(transaction.id, platform);
        }
        window.open(link, '_blank', 'width=600,height=400');
      } catch (error) {
        console.error('Share error:', error);
        // Still open link even if tracking fails
        window.open(link, '_blank', 'width=600,height=400');
      }
    }
  };

  if (!transaction) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Header */}
        <div className="text-center mb-8 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full mb-4 shadow-2xl animate-scaleIn">
            <svg
              className="w-12 h-12 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-4xl font-black bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-2">
            {language === 'fr' ? '✓ Créé!' : '✓ Created!'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {language === 'fr' ? 'Partagez le lien de paiement avec votre acheteur' : 'Share the payment link with your buyer'}
          </p>
        </div>

        {/* Transaction Info */}
        <Card variant="glass" className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
          <div className="space-y-4 mb-6">
            <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{language === 'fr' ? 'Transaction:' : 'Transaction:'}</span>
              <span className="font-bold text-gray-900 dark:text-white">
                #{transaction.shortCode || transaction.id}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 rounded-xl">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{language === 'fr' ? 'Montant:' : 'Amount:'}</span>
              <span className="text-xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                {formatCurrency(transaction.amount || 0, transaction.currency || 'XAF')}
              </span>
            </div>
          </div>

          {/* Share Link */}
          <div className="mb-6">
            <label className="block text-sm font-bold text-gray-900 dark:text-white mb-3">
              {language === 'fr' ? 'Lien de paiement:' : 'Share payment link:'}
            </label>
            <div className="flex gap-2">
              <Input
                type="text"
                value={shortLink || (loading ? (language === 'fr' ? 'Génération...' : 'Generating...') : '')}
                readOnly
                className="flex-1 font-mono"
              />
              <Button
                variant={copied ? 'success' : 'gradient'}
                onClick={handleCopyLink}
                disabled={!shortLink || loading}
                className="shadow-xl hover:shadow-2xl transition-all"
              >
                {copied ? (language === 'fr' ? '✓ Copié' : '✓ Copied') : '📋 Copy'}
              </Button>
            </div>
          </div>

          {/* Social Share Buttons */}
          <div>
            <label className="block text-sm font-bold text-gray-900 dark:text-white mb-3">
              {language === 'fr' ? 'Partager via:' : 'Share via:'}
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { id: 'whatsapp', icon: '📱', name: 'WhatsApp', color: 'from-green-500 to-emerald-500' },
                { id: 'instagram', icon: '📷', name: 'Instagram', color: 'from-pink-500 to-purple-500' },
                { id: 'twitter', icon: '🐦', name: 'Twitter', color: 'from-blue-400 to-cyan-500' },
                { id: 'facebook', icon: '📘', name: 'Facebook', color: 'from-blue-600 to-blue-700' },
                { id: 'email', icon: '📧', name: 'Email', color: 'from-gray-500 to-slate-500' },
                { id: 'sms', icon: '💬', name: 'SMS', color: 'from-yellow-500 to-orange-500' },
              ].map((platform, index) => (
                <Card
                  key={platform.id}
                  variant="outlined"
                  hover
                  clickable
                  onClick={() => handleShare(platform.id)}
                  className={`flex flex-col items-center justify-center p-4 bg-gradient-to-br ${platform.color} bg-opacity-10 hover:bg-opacity-20 border-2 border-transparent hover:border-current transition-all animate-fadeInUp`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <span className="text-3xl mb-2">{platform.icon}</span>
                  <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                    {platform.name}
                  </span>
                </Card>
              ))}
            </div>
          </div>
        </Card>

        {/* QR Code Section */}
        {shortLink && (
          <div className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.15s' }}>
            <QRCode url={shortLink} size={200} />
          </div>
        )}

        {/* Link Preview */}
        {transaction && (
          <div className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <LinkPreview transaction={transaction} shortLink={shortLink} />
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
          <Button
            variant="gradient"
            size="lg"
            fullWidth
            onClick={() => navigate(`/transactions/${transaction.id}`)}
            className="text-lg py-4 shadow-xl hover:shadow-2xl transition-all"
          >
            {language === 'fr' ? 'Voir les détails' : 'View Transaction Details'} →
          </Button>
          <Button
            variant="outline"
            size="lg"
            fullWidth
            onClick={() => navigate('/dashboard')}
          >
            {language === 'fr' ? 'Aller au tableau de bord' : 'Go to Dashboard'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShareLinks;
